import React from "react";
import { Loader2 } from "lucide-react";

/**
 * Neutral placeholder for any LP Express long-form media (image/video) that
 * has no real asset yet. Renders a bordered box at the intended size with a
 * clearly visible "TODO" label naming the expected asset path, so the asset
 * can be dropped in later without hunting for where it goes.
 */
export function MediaPlaceholder({
  path,
  className,
  style,
  rounded = true,
}: {
  path: string;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
}) {
  return (
    <div
      className={className}
      data-testid={`media-todo-${path}`}
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        minHeight: 80,
        backgroundColor: "#E8E2D6",
        border: "2px dashed #B8AFA0",
        borderRadius: rounded ? 10 : 0,
        color: "#6B6560",
        textAlign: "center",
        padding: 12,
        boxSizing: "border-box",
        ...style,
      }}
    >
      <span
        title="Static placeholder content"
        aria-label="Static placeholder content"
        data-testid="dot-source-placeholder"
        style={{
          position: "absolute",
          top: 6,
          right: 6,
          width: 10,
          height: 10,
          borderRadius: "50%",
          backgroundColor: "#EF4444",
          boxShadow: "0 0 0 3px rgba(255,255,255,0.7), 0 1px 3px rgba(0,0,0,0.3)",
          zIndex: 2,
        }}
      />
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          fontFamily: "monospace",
          wordBreak: "break-all",
          lineHeight: 1.4,
        }}
      >
        TODO: {path}
      </span>
    </div>
  );
}

/**
 * Renders a real <img> when a source URL is available, otherwise falls back to
 * the dashed MediaPlaceholder for the same asset path. Used to wire real brand
 * logo / product flat-lay images into the LP Express long-form template while
 * keeping the original placeholder for missing data. Layout footprint matches
 * the placeholder (full width, caller-supplied height via `style`).
 */
export function LpImage({
  src,
  alt = "",
  path,
  className,
  style,
  rounded = true,
  objectFit = "cover",
}: {
  src?: string | null;
  alt?: string;
  path: string;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
  objectFit?: React.CSSProperties["objectFit"];
}) {
  if (src) {
    return (
      <img
        src={src}
        alt={alt}
        className={className}
        data-testid={`img-${path}`}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit,
          borderRadius: rounded ? 10 : 0,
          ...style,
        }}
      />
    );
  }
  return <MediaPlaceholder path={path} className={className} style={style} rounded={rounded} />;
}

/**
 * Renders a real autoplaying muted <video> when a durable source URL is
 * available, otherwise falls back to the dashed MediaPlaceholder for the same
 * asset path. Used to wire a brand's own (R2-mirrored) Instagram videos into the
 * LP Express long-form template while keeping the original /farm-video-*.mp4
 * placeholders for brands with no videos. Layout footprint matches the
 * placeholder (full width, caller-supplied size via `style`).
 */
export function LpVideo({
  src,
  path,
  className,
  style,
  rounded = true,
  objectFit = "cover",
  loading = false,
}: {
  src?: string | null;
  path: string;
  className?: string;
  style?: React.CSSProperties;
  rounded?: boolean;
  objectFit?: React.CSSProperties["objectFit"];
  // True while the brand's Instagram videos are being fetched/mirrored. When the
  // slot has no durable URL yet but a fetch is in flight, show a loading spinner
  // instead of the dashed TODO placeholder.
  loading?: boolean;
}) {
  const [loaded, setLoaded] = React.useState(false);
  const [errored, setErrored] = React.useState(false);

  React.useEffect(() => {
    setLoaded(false);
    setErrored(false);
  }, [src]);

  // No source, or the source failed to load: nothing is actually going to load.
  if (!src || errored) {
    // A brand-video fetch is still in progress — show a spinner (not the dashed
    // TODO box) so the slot reads as "loading" rather than "missing asset".
    if (loading && !errored) {
      return (
        <div
          className={className}
          data-testid={`video-fetching-${path}`}
          aria-label="Loading video"
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            minHeight: 80,
            backgroundColor: "#E8E2D6",
            color: "#6B6560",
            borderRadius: rounded ? 10 : 0,
            boxSizing: "border-box",
            ...style,
          }}
        >
          <Loader2 className="animate-spin" size={28} strokeWidth={2.25} />
        </div>
      );
    }
    return <MediaPlaceholder path={path} className={className} style={style} rounded={rounded} />;
  }

  return (
    <div
      className={className}
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        borderRadius: rounded ? 10 : 0,
        overflow: "hidden",
        ...style,
      }}
    >
      {!loaded && (
        <div
          data-testid={`video-loading-${path}`}
          aria-label="Loading video"
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#E8E2D6",
            color: "#6B6560",
            zIndex: 1,
          }}
        >
          <Loader2 className="animate-spin" size={28} strokeWidth={2.25} />
        </div>
      )}
      <video
        src={src}
        data-testid={`video-${path}`}
        autoPlay
        muted
        loop
        playsInline
        controls
        preload="metadata"
        onLoadedData={() => setLoaded(true)}
        onCanPlay={() => setLoaded(true)}
        onError={() => setErrored(true)}
        style={{
          display: "block",
          width: "100%",
          height: "100%",
          objectFit,
        }}
      />
    </div>
  );
}
